import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createEmptyLiveSessionState,
  getControlTokenFromHash,
  getRelayUrlFromSearch,
  getRoomIdFromSearch,
} from '../shared/liveProtocol';

const initialStatus = {
  connected: false,
  hasSnapshot: false,
  relayUrl: '',
  roomId: '',
  project: null,
  live: createEmptyLiveSessionState(),
  connectionError: null,
  serverClockOffsetMs: 0,
};

const getResolvedRoomId = (roomId) => (
  typeof roomId === 'string' && roomId.trim()
    ? roomId.trim()
    : getRoomIdFromSearch(typeof window === 'undefined' ? '' : window.location.search)
);

const getResolvedControlToken = (controlToken) => (
  typeof controlToken === 'string' && controlToken.trim()
    ? controlToken.trim()
    : getControlTokenFromHash(typeof window === 'undefined' ? '' : window.location.hash)
);

const getClockOffset = (serverNow) => (
  Number.isFinite(serverNow) ? (serverNow - Date.now()) : 0
);

const buildLiveStateFromOutput = (output = {}) => ({
  ...createEmptyLiveSessionState(),
  program: output.program || null,
  phase: output.phase || 'idle',
  playbackStartedAt: Number.isFinite(output.playbackStartedAt) ? output.playbackStartedAt : null,
  playbackElapsedMs: Math.max(0, Number(output.playbackElapsedMs) || 0),
});

export const useRelayConnection = ({
  relayUrl,
  roomId,
  controlToken,
  onProjectUpdated,
  clientRole = 'control',
} = {}) => {
  const resolvedRelayUrl = useMemo(
    () => relayUrl || getRelayUrlFromSearch(typeof window === 'undefined' ? '' : window.location.search),
    [relayUrl],
  );
  const resolvedRoomId = useMemo(() => getResolvedRoomId(roomId), [roomId]);
  const resolvedControlToken = useMemo(() => getResolvedControlToken(controlToken), [controlToken]);
  const [status, setStatus] = useState({
    ...initialStatus,
    relayUrl: resolvedRelayUrl,
    roomId: resolvedRoomId,
  });
  const socketRef = useRef(null);
  const onProjectUpdatedRef = useRef(onProjectUpdated);

  useEffect(() => {
    onProjectUpdatedRef.current = onProjectUpdated;
  }, [onProjectUpdated]);

  useEffect(() => {
    setStatus((prev) => ({
      ...prev,
      relayUrl: resolvedRelayUrl,
      roomId: resolvedRoomId,
    }));
  }, [resolvedRelayUrl, resolvedRoomId]);

  useEffect(() => {
    if (!resolvedRelayUrl || !resolvedRoomId || typeof WebSocket === 'undefined') return undefined;

    const socket = new WebSocket(resolvedRelayUrl);
    socketRef.current = socket;

    socket.addEventListener('open', () => {
      const hello = {
        type: 'hello',
        clientRole,
        roomId: resolvedRoomId,
      };
      if (clientRole === 'control' && resolvedControlToken) hello.controlToken = resolvedControlToken;
      socket.send(JSON.stringify(hello));
      setStatus((prev) => ({
        ...prev,
        connected: true,
        hasSnapshot: false,
        relayUrl: resolvedRelayUrl,
        roomId: resolvedRoomId,
        connectionError: null,
      }));
    });

    socket.addEventListener('close', () => {
      setStatus((prev) => ({
        ...prev,
        connected: false,
        hasSnapshot: false,
      }));
    });

    socket.addEventListener('message', (event) => {
      try {
        const message = JSON.parse(event.data);
        const nextClockOffsetMs = getClockOffset(message?.serverNow);

        if (message.type === 'relay:error') {
          setStatus((prev) => ({
            ...prev,
            connectionError: {
              code: message.code || 'UNKNOWN',
              message: message.message || 'Relay connection failed',
            },
            serverClockOffsetMs: nextClockOffsetMs,
          }));
          return;
        }

        if (message.type === 'state:snapshot') {
          setStatus((prev) => ({
            ...prev,
            hasSnapshot: true,
            roomId: message.roomId || resolvedRoomId,
            project: message.project || null,
            live: message.live || createEmptyLiveSessionState(),
            connectionError: null,
            serverClockOffsetMs: nextClockOffsetMs,
          }));
          if (message.project && onProjectUpdatedRef.current) onProjectUpdatedRef.current(message.project);
          return;
        }

        if (message.type === 'output:snapshot') {
          setStatus((prev) => ({
            ...prev,
            hasSnapshot: true,
            roomId: message.roomId || resolvedRoomId,
            project: null,
            live: buildLiveStateFromOutput(message.output),
            connectionError: null,
            serverClockOffsetMs: nextClockOffsetMs,
          }));
          return;
        }

        if (message.type === 'project:updated') {
          setStatus((prev) => ({
            ...prev,
            project: message.project || null,
            serverClockOffsetMs: nextClockOffsetMs,
          }));
          if (message.project && onProjectUpdatedRef.current) onProjectUpdatedRef.current(message.project);
          return;
        }

        if (message.type === 'live:updated') {
          setStatus((prev) => ({
            ...prev,
            live: message.live || createEmptyLiveSessionState(),
            serverClockOffsetMs: nextClockOffsetMs,
          }));
          return;
        }

        if (message.type === 'output:updated') {
          setStatus((prev) => ({
            ...prev,
            live: buildLiveStateFromOutput(message.output),
            serverClockOffsetMs: nextClockOffsetMs,
          }));
        }
      } catch {
        // Ignore malformed relay messages.
      }
    });

    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, [clientRole, resolvedControlToken, resolvedRelayUrl, resolvedRoomId]);

  const send = useCallback((payload) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify(payload));
    return true;
  }, []);

  return {
    ...status,
    send,
  };
};
